import type {
  QuoteBudgetAuditIssue,
  QuoteBudgetCostBreakdown,
  QuoteBudgetLine,
  QuoteBudgetOrder,
  QuoteBudgetPeopleCounts,
  QuoteBudgetSummary,
} from "../types";
import {
  deriveItineraryResourceQuantity,
} from "../../../shared/itinerary-resource-entry";
import { BUS_VEHICLE_SEAT_OPTIONS } from "../../../shared/transport/busVehicleCatalog";

/** 包车燃油地区口径。 */
export type BusFuelRegion = "regular" | "sanzhou";

/** 包车燃油设置行。 */
export type BusFuelRow = {
  seat: number;
  mappedSeat: number;
  count: number;
  distanceKm: number;
  regularRate: string | number;
};

/** 包车燃油金额分解，全部使用两位小数字符串避免浮点精度丢失。 */
export type BusFuelMoneyBreakdown = {
  fuelAmount: string;
  tollAmount: string;
  totalAmount: string;
};

type BusDailyFixedLine = {
  amount: string;
  basis: string;
};

const BUS_FUEL_REGULAR_RATES: Record<number, number> = {
  17: 1.0,
  19: 1.1,
  23: 1.3,
  30: 1.7,
  39: 1.9,
  49: 2.0,
  50: 2.0,
  55: 2.3,
  56: 2.5,
};

const BUS_FUEL_SANZHOU_ADDONS: Record<number, number> = {
  17: 0.1,
  19: 0.1,
  23: 0.1,
  30: 0.2,
  39: 0.2,
  49: 0.3,
  50: 0.3,
  55: 0.3,
  56: 0.4,
};

const BUS_FUEL_SEAT_MAPPING: Record<number, number> = {
  37: 39,
  47: 49,
  48: 49,
  53: 55,
};

const BUS_DAILY_FIXED_RATES: Record<number, Record<number, number>> = {
  17: { 1: 500, 2: 500, 3: 600, 4: 700, 5: 600, 6: 700, 7: 700, 8: 700, 9: 800, 10: 900, 11: 900, 12: 600 },
  23: { 1: 550, 2: 550, 3: 650, 4: 750, 5: 700, 6: 750, 7: 750, 8: 750, 9: 800, 10: 950, 11: 1000, 12: 650 },
  30: { 1: 600, 2: 600, 3: 750, 4: 900, 5: 700, 6: 700, 7: 700, 8: 700, 9: 800, 10: 700, 11: 1000, 12: 750 },
  39: { 1: 650, 2: 650, 3: 750, 4: 1000, 5: 750, 6: 800, 7: 800, 8: 800, 9: 900, 10: 800, 11: 1100, 12: 750 },
  49: { 1: 800, 2: 800, 3: 900, 4: 1200, 5: 900, 6: 1000, 7: 900, 8: 900, 9: 1000, 10: 900, 11: 1200, 12: 900 },
  55: { 1: 900, 2: 900, 3: 1000, 4: 1300, 5: 1000, 6: 1100, 7: 1000, 8: 1000, 9: 1100, 10: 1000, 11: 1300, 12: 1000 },
  56: { 1: 950, 2: 950, 3: 1050, 4: 1400, 5: 1050, 6: 1100, 7: 1050, 8: 1050, 9: 1200, 10: 1050, 11: 1400, 12: 1050 },
};

/** 把字符串/数字安全转为有限数值，非法值视为 0。 */
function toNumber(value: string | number | null | undefined): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** 去尾随零的纯数字串，用于公式中的公里数与单价展示。 */
function plainNumber(value: string | number): string {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : "0";
}

/**
 * 金额规整为两位小数字符串；非有限值归零。
 * @param value 原始金额
 */
export function normalizeMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "0.00";
  return formatDerivedMoney(decimalToCents(value) ?? 0n);
}

/** 按总人数选择大巴车型组合：车辆数最少，同车数下空座最少。 */
function buildBusFleet(totalPeople: number): { desc: string; quantity: number } {
  const people = Math.max(1, totalPeople);
  const vehicleCount = Math.ceil(people / 55);
  const maxEmptySeats = vehicleCount * 55 - people;
  const emptySeats = BUS_VEHICLE_SEAT_OPTIONS.map((seat) => 55 - seat);
  let states: Array<number[] | null> = Array.from({ length: maxEmptySeats + 1 }, () => null);
  states[0] = Array(BUS_VEHICLE_SEAT_OPTIONS.length).fill(0);

  // 最少车辆数由 55 座上界直接确定；DP 只搜索末尾最多 54 个空座，人数再大也保持线性。
  for (let vehicle = 0; vehicle < vehicleCount; vehicle += 1) {
    const next: Array<number[] | null> = Array.from({ length: maxEmptySeats + 1 }, () => null);
    for (let empty = 0; empty <= maxEmptySeats; empty += 1) {
      const counts = states[empty];
      if (!counts) continue;
      for (let index = 0; index < BUS_VEHICLE_SEAT_OPTIONS.length; index += 1) {
        const candidateEmpty = empty + emptySeats[index];
        if (candidateEmpty > maxEmptySeats || next[candidateEmpty]) continue;
        const candidate = [...counts];
        candidate[index] += 1;
        next[candidateEmpty] = candidate;
      }
    }
    states = next;
  }
  let bestEmptySeats = maxEmptySeats;
  while (bestEmptySeats > 0 && !states[bestEmptySeats]) bestEmptySeats -= 1;
  const selected = states[bestEmptySeats] ?? BUS_VEHICLE_SEAT_OPTIONS.map((seat) => (
    seat === 55 ? vehicleCount : 0
  ));
  const counts = new Map<number, number>();
  selected.forEach((count, index) => {
    if (count > 0) counts.set(BUS_VEHICLE_SEAT_OPTIONS[index], count);
  });
  const parts = [...counts.entries()]
    .sort(([a], [b]) => b - a)
    .map(([seat, count]) => `${seat}座 x ${count}辆`);
  return {
    desc: parts.join(" + "),
    quantity: vehicleCount,
  };
}

/** 图片外车型按确认映射归并到最近车型。 */
export function mapBusFuelSeat(seat: number): number {
  return BUS_FUEL_SEAT_MAPPING[seat] ?? seat;
}

/** 解析包车规格中的车型组合与公里数。 */
export function parseBusFuelRows(line: Pick<QuoteBudgetLine, "resourceType" | "itemSpecSnapshot">): BusFuelRow[] {
  if (line.resourceType !== "包车") {
    return [];
  }
  const spec = String(line.itemSpecSnapshot ?? "");
  const distance = Number(spec.match(/(\d+(?:\.\d+)?)km/)?.[1]);
  if (!Number.isFinite(distance) || distance <= 0) {
    return [];
  }
  return Array.from(spec.matchAll(/(\d+)座\s*x\s*(\d+)辆/g))
    .map((match) => {
      const seat = Number(match[1]);
      const mappedSeat = mapBusFuelSeat(seat);
      return {
        seat,
        mappedSeat,
        count: Number(match[2]),
        distanceKm: distance,
        regularRate: BUS_FUEL_REGULAR_RATES[mappedSeat] ?? 0,
      };
    })
    .filter((row) => row.regularRate > 0 && row.count > 0);
}

/** 三州相对常规口径的每公里加价。 */
export function busFuelRegionAddon(row: BusFuelRow, region: BusFuelRegion): number {
  return region === "sanzhou" ? (BUS_FUEL_SANZHOU_ADDONS[row.mappedSeat] ?? 0) : 0;
}

/** 当前地区下的实际油耗单价文本，合法值按整数分规整且不经过 Number。 */
export function busFuelEffectiveRateText(
  row: BusFuelRow,
  region: BusFuelRegion,
): string | null {
  const cents = busFuelEffectiveRateCents(row, region);
  return cents === null ? null : formatPlainMoneyCents(cents);
}

/**
 * 精确计算包车燃油、高速及合计金额。
 * 公里数与每公里单价都按两位小数缩放，所有车型合计后再 ROUND_HALF_UP 到分。
 */
export function calculateBusFuelMoney(
  rows: BusFuelRow[],
  region: BusFuelRegion,
  tollAmount: string | number = 0,
): BusFuelMoneyBreakdown | null {
  let fuelNumerator = 0n;
  for (const row of rows) {
    const distanceCents = decimalToCents(row.distanceKm);
    const rateCents = busFuelEffectiveRateCents(row, region);
    if (
      distanceCents === null
      || distanceCents <= 0n
      || rateCents === null
      || !Number.isSafeInteger(row.count)
      || row.count <= 0
    ) return null;
    fuelNumerator += distanceCents * rateCents * BigInt(row.count);
  }
  const fuelCents = divideHalfUp(fuelNumerator, 100n);
  const tollText = String(tollAmount).trim();
  const tollCents = decimalToCents(tollText || 0);
  if (tollCents === null || tollCents < 0n) return null;
  const totalCents = fuelCents + tollCents;
  if (
    fuelCents < 0n
    || fuelCents > MAX_SAFE_BUS_MONEY_CENTS
    || tollCents > MAX_SAFE_BUS_MONEY_CENTS
    || totalCents > MAX_SAFE_BUS_MONEY_CENTS
  ) return null;
  return {
    fuelAmount: formatMoneyCents(fuelCents),
    tollAmount: formatMoneyCents(tollCents),
    totalAmount: formatMoneyCents(totalCents),
  };
}

/** 把合法金额规整为公式中的简洁十进制文本。 */
export function normalizeMoneyPlain(value: string | number): string | null {
  const cents = decimalToCents(value);
  return cents === null ? null : formatPlainMoneyCents(cents);
}

/** 精确汇总统一包车基础费；超出前端安全金额边界时返回 null。 */
export function calculateBusBaseFeeAmount(
  entries: ReadonlyArray<{
    price: string | number;
    count: number;
    dayCount?: string | number;
  }>,
  dayCount: string | number = 1,
): string | null {
  if (entries.length === 0) return null;
  let totalNumerator = 0n;
  for (const entry of entries) {
    const priceCents = decimalToCents(entry.price);
    const dayCountCents = decimalToCents(entry.dayCount ?? dayCount);
    if (
      priceCents === null
      || priceCents < 0n
      || dayCountCents === null
      || dayCountCents <= 0n
      || !Number.isSafeInteger(entry.count)
      || entry.count <= 0
    ) return null;
    totalNumerator += priceCents * dayCountCents * BigInt(entry.count);
  }
  const totalCents = divideHalfUp(totalNumerator, 100n);
  return totalCents <= MAX_SAFE_BUS_MONEY_CENTS
    ? formatMoneyCents(totalCents)
    : null;
}

/** 构造包车燃油计价依据。 */
export function buildBusFuelBasis(rows: BusFuelRow[], region: BusFuelRegion): string {
  const details = rows.map((row) => {
    const rate = busFuelEffectiveRateText(row, region) ?? "0";
    const mapped = row.seat === row.mappedSeat ? "" : `按${row.mappedSeat}座`;
    return `${row.seat}座${mapped} ${plainNumber(row.distanceKm)}km x ${rate}元/km x ${row.count}辆`;
  });
  return `按车型公里油费估算：${region === "sanzhou" ? "三州" : "常规"}；${details.join("；")}`;
}

/** 从固定费依据中读取服务月份。 */
function busDailyFixedMonth(line: QuoteBudgetLine): number | null {
  const month = Number(String(line.priceBasisDesc ?? "").match(/(?:^|\D)(1[0-2]|[1-9])月/)?.[1]);
  return Number.isInteger(month) && month >= 1 && month <= 12 ? month : null;
}

/** 按车型组合和月份生成每日固定用车费分项，用于汇总单行金额和依据。 */
function buildBusDailyFixedFeeLines(fleetDesc: string, month: number): BusDailyFixedLine[] {
  return Array.from(fleetDesc.matchAll(/(\d+)座\s*x\s*(\d+)辆/g))
    .map((match) => {
      const seat = Number(match[1]);
      const count = Number(match[2]);
      const mappedSeat = mapBusFuelSeat(seat);
      const rate = BUS_DAILY_FIXED_RATES[mappedSeat]?.[month] ?? 0;
      if (!rate || !count) return null;
      const mapped = seat === mappedSeat ? "" : `按${mappedSeat}座`;
      return {
        amount: normalizeMoney(rate * count),
        basis: `按车型每日用车固定费：${seat}座${mapped} ${month}月 ${plainNumber(rate)}元/天 x ${count}辆`,
      };
    })
    .filter((line): line is BusDailyFixedLine => Boolean(line));
}

/** 保留燃油费行当前地区口径。 */
export function busFuelRegionFromBasis(line: QuoteBudgetLine): BusFuelRegion {
  return String(line.priceBasisDesc ?? "").includes("按车型公里油费估算：三州") ? "sanzhou" : "regular";
}

/** 从包车规格读取上次计算时的人数。 */
function busSpecPeople(line: QuoteBudgetLine): number | null {
  const people = Number(String(line.itemSpecSnapshot ?? "").match(/总人数(\d+)人/)?.[1]);
  return Number.isInteger(people) && people > 0 ? people : null;
}

/** 读取固定费行用车天数，优先 dayCount，兼容依据里的用车天数标记。 */
function busDailyFixedDayCount(line: QuoteBudgetLine): number {
  const fromDayCount = toNumber(line.dayCount);
  if (fromDayCount > 0) return fromDayCount;
  const fromBasis = Number(String(line.priceBasisDesc ?? "").match(/用车天数=(\d+(?:\.\d+)?)/)?.[1]);
  if (Number.isFinite(fromBasis) && fromBasis > 0) return fromBasis;
  return Math.max(1, toNumber(line.quantity));
}

/** 读取固定费中各月份实际计费天数；单月旧数据回落到整行天数。 */
function busDailyFixedPeriods(line: QuoteBudgetLine): Array<{ month: number; dayCount: number }> {
  const months = new Set<number>();
  const explicitDays = new Map<number, number>();
  for (const match of String(line.priceBasisDesc ?? "").matchAll(
    /((?:1[0-2]|[1-9]))月[^；]*?\d+(?:\.\d+)?元\/天\s*x\s*\d+辆(?:\s*x\s*(\d+(?:\.\d+)?)天)?/g,
  )) {
    const month = Number(match[1]);
    const dayCount = Number(match[2]);
    months.add(month);
    if (Number.isFinite(dayCount) && dayCount > 0) {
      explicitDays.set(month, Math.max(explicitDays.get(month) ?? 0, dayCount));
    }
  }
  if (explicitDays.size > 0) {
    return Array.from(explicitDays, ([month, dayCount]) => ({ month, dayCount }));
  }
  const month = months.values().next().value ?? busDailyFixedMonth(line);
  return month ? [{ month, dayCount: busDailyFixedDayCount(line) }] : [];
}

/** 判断是否为包车每日固定费行。 */
function isBusDailyFixedLine(line: QuoteBudgetLine): boolean {
  return line.resourceType === "包车" && line.resourceEntryType === "包车固定费";
}

/** 从固定费行名提取用车日，无法识别时不做跨行归并。 */
function busDailyFixedDayKey(line: QuoteBudgetLine): string | null {
  return String(line.itemNameSnapshot ?? "").match(/^(D\d+(?:-D\d+)?)/)?.[1] ?? null;
}

/** 从行名提取 D 天次范围。 */
function busDayRange(line: QuoteBudgetLine): { label: string; days: number[] } | null {
  const matched = String(line.itemNameSnapshot ?? "").match(/^D(\d+)(?:-D(\d+))?/);
  if (!matched) return null;
  const start = Number(matched[1]);
  const end = Number(matched[2] ?? matched[1]);
  if (!Number.isInteger(start) || !Number.isInteger(end) || start <= 0 || end < start) return null;
  return {
    label: start === end ? `D${start}` : `D${start}-D${end}`,
    days: Array.from({ length: end - start + 1 }, (_, index) => start + index),
  };
}

/** 包车燃油核价段是固定费重排的骨架。 */
function busPricingSegment(line: QuoteBudgetLine): { label: string; days: number[] } | null {
  if (line.resourceType !== "包车" || isBusDailyFixedLine(line)) return null;
  if (line.resourceEntryType !== "包车燃油费" && parseBusFuelRows(line).length === 0) return null;
  return busDayRange(line);
}

/** 读取固定费行规格中的车型组合，并按座位数降序稳定展示。 */
function busDailyFixedFleetDesc(rows: QuoteBudgetLine[]): string {
  const vehicles = new Map<number, number>();
  for (const line of rows) {
    for (const match of String(line.itemSpecSnapshot ?? "").matchAll(/(\d+)座\s*x\s*(\d+)辆/g)) {
      const seat = Number(match[1]);
      const count = Number(match[2]);
      if (seat > 0 && count > 0) {
        vehicles.set(seat, Math.max(vehicles.get(seat) ?? 0, count));
      }
    }
  }
  return Array.from(vehicles)
    .sort(([seatA], [seatB]) => seatB - seatA)
    .map(([seat, count]) => `${seat}座 x ${count}辆`)
    .join(" + ");
}

/** 多条同段固定费旧行合并为一条，保留首行 ID 以便保存时替换旧草稿。 */
function mergeBusDailyFixedRows(rows: QuoteBudgetLine[]): QuoteBudgetLine {
  const first = rows[0];
  const mergedAmountCents = rows.reduce(
    (sum, line) => sum + moneyCents(String(line.budgetAmount || calculateLineAmount(line))),
    0n,
  );
  const amountCents = absoluteBigInt(mergedAmountCents) <= MAX_NUMERIC_18_2_CENTS
    ? mergedAmountCents
    : 0n;
  const amount = formatMoneyCents(amountCents);
  const fixedDays = new Set(rows.flatMap((line) => busDayRange(line)?.days ?? []));
  const dayCount =
    fixedDays.size > 0
      ? fixedDays.size
      : Math.max(1, rows.reduce((sum, line) => sum + busDailyFixedDayCount(line), 0));
  const sortedDays = [...fixedDays].sort((a, b) => a - b);
  const day = sortedDays.length > 0
    ? sortedDays[0] === sortedDays.at(-1)
      ? `D${sortedDays[0]}`
      : `D${sortedDays[0]}-D${sortedDays.at(-1)}`
    : busDailyFixedDayKey(first);
  const people = rows.map((line) => busSpecPeople(line)).find((value) => value !== null);
  const fleetDesc = busDailyFixedFleetDesc(rows);
  const basisDayCounts = new Map<string, number>();
  for (const line of rows) {
    for (const part of String(line.priceBasisDesc ?? "")
      .replace(/^按车型每日用车固定费：/, "")
      .split("；")
      .filter((item) => item && !item.startsWith("用车天数="))) {
      const explicitDays = Number(part.match(/\s+x\s*(\d+(?:\.\d+)?)天$/)?.[1]);
      const basis = part.replace(/\s+x\s*\d+(?:\.\d+)?天$/, "");
      const days = Number.isFinite(explicitDays) ? explicitDays : busDailyFixedDayCount(line);
      basisDayCounts.set(basis, (basisDayCounts.get(basis) ?? 0) + days);
    }
  }
  const months = new Set(
    Array.from(basisDayCounts.keys(), (basis) => basis.match(/(?:1[0-2]|[1-9])月/)?.[0]).filter(
      Boolean,
    ),
  );
  const basisDetails = Array.from(basisDayCounts, ([basis, days]) =>
    months.size > 1 ? `${basis} x ${plainNumber(days)}天` : basis,
  );
  const dayBasis = dayCount > 1 ? [`用车天数=${dayCount}`] : [];
  const dayCountCents = decimalToCents(dayCount) ?? 100n;
  return {
    ...first,
    itemNameSnapshot: day ? `${day} 用车固定费` : first.itemNameSnapshot,
    itemSpecSnapshot: fleetDesc
      ? `${fleetDesc}${people ? `；总人数${people}人` : ""}`
      : first.itemSpecSnapshot,
    quantity: String(dayCount),
    dayCount,
    budgetUnitPrice: formatDerivedMoney(
      divideHalfUp(amountCents * 100n, dayCountCents),
    ),
    budgetAmount: amount,
    priceBasisDesc:
      basisDetails.length > 0
        ? `按车型每日用车固定费：${[...dayBasis, ...basisDetails].join("；")}`
        : first.priceBasisDesc,
  };
}

/** 仅把日期连续且计价口径一致的旧固定费行合并，避免扩大真实用车日期。 */
function groupMergeableBusDailyFixedRows(rows: QuoteBudgetLine[]): QuoteBudgetLine[][] {
  const sorted = [...rows].sort(
    (left, right) =>
      (busDayRange(left)?.days[0] ?? Number.MAX_SAFE_INTEGER)
      - (busDayRange(right)?.days[0] ?? Number.MAX_SAFE_INTEGER),
  );
  const sameDayGroups = new Map<string, QuoteBudgetLine[]>();
  for (const line of sorted) {
    const label = busDayRange(line)?.label ?? `line:${line.budgetLineId}`;
    sameDayGroups.set(label, [...(sameDayGroups.get(label) ?? []), line]);
  }
  const dailyRows = Array.from(sameDayGroups.values(), mergeBusDailyFixedRows);
  const groups: QuoteBudgetLine[][] = [];
  for (const line of dailyRows) {
    const current = busDayRange(line);
    const group = groups.at(-1);
    const previous = group?.at(-1);
    const previousRange = previous ? busDayRange(previous) : null;
    const samePricing = previous
      && previous.itemSpecSnapshot === line.itemSpecSnapshot
      && normalizeMoney(previous.budgetUnitPrice) === normalizeMoney(line.budgetUnitPrice)
      && previous.priceBasisDesc === line.priceBasisDesc;
    if (
      group
      && current
      && previousRange
      && previousRange.days.at(-1)! + 1 === current.days[0]
      && samePricing
    ) {
      group.push(line);
    } else {
      groups.push([line]);
    }
  }
  return groups;
}

/** 兼容旧草稿：固定费跟随燃油核价段重排，只归并连续且同价的实际用车日。 */
export function normalizeBusDailyFixedRows(lines: QuoteBudgetLine[]): QuoteBudgetLine[] {
  const segments = lines
    .map(busPricingSegment)
    .filter((segment): segment is { label: string; days: number[] } => Boolean(segment));
  const groups = new Map<string, { label: string; rows: QuoteBudgetLine[] }>();
  for (const line of lines) {
    const range = isBusDailyFixedLine(line) ? busDayRange(line) : null;
    if (!range) continue;
    const segment = segments.find((candidate) => range.days.some((day) => candidate.days.includes(day)));
    const label = segment?.label ?? range.label;
    const group = groups.get(label) ?? { label, rows: [] };
    group.rows.push(line);
    groups.set(label, group);
  }
  const emitted = new Set<string>();
  const normalized = lines.flatMap((line) => {
    if (isBusDailyFixedLine(line)) {
      return busDayRange(line) ? [] : [line];
    }
    const result = [line];
    const segment = busPricingSegment(line);
    const group = segment ? groups.get(segment.label) : null;
    if (segment && group && !emitted.has(segment.label)) {
      result.push(...groupMergeableBusDailyFixedRows(group.rows).map(mergeBusDailyFixedRows));
      emitted.add(segment.label);
    }
    return result;
  });
  for (const [label, group] of groups) {
    if (!emitted.has(label)) {
      normalized.push(...groupMergeableBusDailyFixedRows(group.rows).map(mergeBusDailyFixedRows));
    }
  }
  return normalized;
}

/** 构造行内公式回写文本，复用现有 applyLineFormula 管道。 */
export function buildBusFuelFormula(
  rows: BusFuelRow[],
  region: BusFuelRegion,
  options?: { tollAmount?: string | number; segmentDetailBasis?: string },
): string {
  const amounts = calculateBusFuelMoney(rows, region, options?.tollAmount ?? 0) ?? {
    fuelAmount: "0.00",
    tollAmount: "0.00",
    totalAmount: "0.00",
  };
  const calculation = options
    ? `；燃油合计${amounts.fuelAmount}元 + 高速费${amounts.tollAmount}元 = ${amounts.totalAmount}元`
    : "";
  const details = options?.segmentDetailBasis ? `；${options.segmentDetailBasis}` : "";
  return `包车燃油费=${amounts.totalAmount}；${buildBusFuelBasis(rows, region).replace(
    "按车型公里油费估算：",
    "",
  )}${calculation}${details}`;
}

/** 解析包车燃油公式文本。 */
export function parseBusFuelFormula(text: string): { amount: string; basis: string } | null {
  const matched = text.match(/^包车燃油费=(\d+(?:\.\d+)?)(?:[；;](.+))?$/);
  if (!matched) {
    return null;
  }
  return {
    amount: normalizeMoney(matched[1]),
    basis: `按车型公里油费估算：${matched[2] ?? ""}`,
  };
}

/** 解析包车固定费公式文本。 */
export function parseBusDailyFixedFormula(
  text: string,
): { amount: string; unitPrice: string; quantity: string; dayCount: number; basis: string } | null {
  const matched = text.match(/^包车固定费=(\d+(?:\.\d+)?)(?:[；;](.+))?$/);
  if (!matched) {
    return null;
  }
  const amount = normalizeMoney(matched[1]);
  const details = matched[2] ?? "";
  const dayCount = Math.max(1, Number(details.match(/用车天数=(\d+(?:\.\d+)?)/)?.[1]) || 1);
  const dayCountCents = decimalToCents(dayCount) ?? 100n;
  return {
    amount,
    unitPrice: formatDerivedMoney(
      divideHalfUp(moneyCents(amount) * 100n, dayCountCents),
    ),
    quantity: String(dayCount),
    dayCount,
    basis: `按车型每日用车固定费：${details}`,
  };
}

/** 人数变化时推导包车车型组合规格，保留原规格中的公里数。 */
export function deriveBusFleetFromPeople(
  line: QuoteBudgetLine,
  counts: QuoteBudgetPeopleCounts,
): Pick<QuoteBudgetLine, "quantity" | "dayCount" | "itemSpecSnapshot" | "budgetUnitPrice" | "budgetAmount" | "priceBasisDesc"> | null {
  if (line.resourceType !== "包车" || !line.itemSpecSnapshot) return null;
  const totalPeople =
    toNumber(counts.studentCount) +
    toNumber(counts.teacherCount) +
    toNumber(counts.guideCount) +
    toNumber(counts.driverCount);
  const displayPeople = Math.max(1, totalPeople);
  if (busSpecPeople(line) === displayPeople) return null;
  const fleet = buildBusFleet(totalPeople);
  if (line.resourceEntryType === "包车固定费") {
    const periods = busDailyFixedPeriods(line);
    if (periods.length === 0) return null;
    const dayCount = busDailyFixedDayCount(line);
    let totalAmountValue = 0;
    const details: string[] = [];
    for (const period of periods) {
      const fixedLines = buildBusDailyFixedFeeLines(fleet.desc, period.month);
      if (fixedLines.length === 0) return null;
      totalAmountValue +=
        fixedLines.reduce((sum, fixedLine) => sum + Number(fixedLine.amount), 0) * period.dayCount;
      details.push(
        ...fixedLines.map((fixedLine) => {
          const basis = fixedLine.basis.replace("按车型每日用车固定费：", "");
          return periods.length > 1 ? `${basis} x ${plainNumber(period.dayCount)}天` : basis;
        }),
      );
    }
    const totalAmount = normalizeMoney(totalAmountValue);
    const amount = normalizeMoney(totalAmountValue / dayCount);
    const days = dayCount > 1 ? "用车天数=" + dayCount + "；" : "";
    return {
      quantity: String(dayCount),
      dayCount,
      itemSpecSnapshot: `${fleet.desc}；总人数${displayPeople}人`,
      budgetUnitPrice: amount,
      budgetAmount: totalAmount,
      priceBasisDesc: `按车型每日用车固定费：${days}${details.join("；")}`,
    };
  }
  const distance = line.itemSpecSnapshot.match(/(\d+(?:\.\d+)?)km/)?.[0];
  const tollSpec = line.itemSpecSnapshot.match(
    /；高速\d+(?:\.\d+)?km；高速费\d+(?:\.\d+)?元/,
  )?.[0];
  const next: QuoteBudgetLine = {
    ...line,
    quantity: "1",
    itemSpecSnapshot: `${fleet.desc}${distance ? `；${distance}` : ""}${tollSpec ?? ""}；总人数${displayPeople}人`,
  };
  const rows = parseBusFuelRows(next);
  if (rows.length === 0) return null;
  const region = busFuelRegionFromBasis(line);
  const detailMarker = "原始大巴线段：";
  const detailIndex = String(line.priceBasisDesc ?? "").indexOf(detailMarker);
  const segmentDetailBasis =
    detailIndex >= 0 ? String(line.priceBasisDesc).slice(detailIndex) : undefined;
  const formula = buildBusFuelFormula(
    rows,
    region,
    tollSpec || segmentDetailBasis
      ? {
          tollAmount: line.itemSpecSnapshot.match(/高速费(\d+(?:\.\d+)?)元/)?.[1] ?? "0",
          segmentDetailBasis,
        }
      : undefined,
  );
  const fuel = parseBusFuelFormula(formula);
  if (!fuel) return null;
  return {
    quantity: next.quantity,
    itemSpecSnapshot: next.itemSpecSnapshot,
    budgetUnitPrice: fuel.amount,
    budgetAmount: fuel.amount,
    priceBasisDesc: fuel.basis,
  };
}

/**
 * 人数变化时推导预算行数量；非人数联动行保持原数量。
 * @param line 预算行
 * @param counts 当前人数集合
 */
export function deriveQuantityFromPeople(
  line: QuoteBudgetLine,
  counts: QuoteBudgetPeopleCounts,
): number {
  if (line.entrySnapshot?.inputMode === "manual_v2") {
    if (line.entrySnapshot.quantityMode !== "linked_people") return toNumber(line.quantity);
    const roleCounts: Record<string, number> = {
      学生: counts.studentCount, student: counts.studentCount,
      老师: counts.teacherCount, teacher: counts.teacherCount,
      导游: counts.guideCount ?? 0, guide: counts.guideCount ?? 0,
      司机: counts.driverCount ?? 0, driver: counts.driverCount ?? 0,
    };
    return (roleCounts[line.travelerRole ?? ""] ?? 0) * line.entrySnapshot.billingCount;
  }
  const priceBasis = String(line.priceBasisDesc ?? "");
  const sourcePrefix = "自然语言录入：";
  const quantity = deriveItineraryResourceQuantity(
    {
      unit: line.unit,
      travelerRole: line.travelerRole ?? null,
      isLinkedToPeopleCount: line.isLinkedToPeopleCount,
      nightCount: line.nightCount ?? null,
      dayCount: line.dayCount ?? null,
      mealCount: null,
      sourceText: priceBasis.startsWith(sourcePrefix) ? priceBasis.slice(sourcePrefix.length) : "",
      quantity: line.quantity,
    },
    {
      studentCount: toNumber(counts.studentCount),
      teacherCount: toNumber(counts.teacherCount),
      guideCount: toNumber(counts.guideCount),
      driverCount: toNumber(counts.driverCount),
    },
  );
  if (
    line.resourceType === "大交通"
    && line.resourceEntryType === "手续费"
    && line.isLinkedToPeopleCount
  ) {
    const times = Number(line.itemSpecSnapshot?.match(/^([1-9]\d*)次$/)?.[1]);
    return quantity * (times > 0 ? times : 1);
  }
  return quantity;
}

/**
 * 计算单行金额 = 数量 * 单价。
 * @param line 预算行
 */
export function calculateLineAmount(line: QuoteBudgetLine): string {
  const quantityCents = decimalToCents(line.quantity) ?? 0n;
  const unitPriceCents = decimalToCents(line.budgetUnitPrice) ?? 0n;
  const conversion = line.entrySnapshot?.conversion;
  if (conversion && quantityCents === decimalToCents(conversion.quantity)
    && unitPriceCents === decimalToCents(conversion.unitPrice)) {
    const amount = decimalToCents(conversion.amount);
    if (amount !== null) return formatMoneyCents(amount);
  }
  const scaledAmount = quantityCents * unitPriceCents;
  if (absoluteBigInt(scaledAmount) > MAX_NUMERIC_18_2_CENTS * 100n) return "0.00";
  return formatMoneyCents(divideHalfUp(scaledAmount, 100n));
}

/** 把已规整的两位小数金额转为整数分，避免多行浮点累加跨过半元边界。 */
function moneyCents(value: string): bigint {
  return decimalToCents(value) ?? 0n;
}

const MAX_NUMERIC_18_2_INTEGER = 9_999_999_999_999_999n;
const MAX_NUMERIC_18_2_CENTS = 999_999_999_999_999_999n;
const MAX_SAFE_BUS_MONEY_CENTS = 9_007_199_254_740_900n;
const MAX_SAVE_DRAFT_LINE_NO = 2_147_483_646;

/** 返回 BigInt 的绝对值。 */
function absoluteBigInt(value: bigint): bigint {
  return value < 0n ? -value : value;
}

/** 按 MySQL DECIMAL(18,2) 边界把十进制文本规整为整数分。 */
function decimalToCents(value: string | number): bigint | null {
  const matched = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!matched) return null;
  const integerText = matched[2].replace(/^0+(?=\d)/, "");
  if (integerText.length > 16) return null;
  const integer = BigInt(integerText);
  const fraction = matched[3] ?? "";
  const hundredthsText = `${fraction}00`.slice(0, 2);
  if (
    integer > MAX_NUMERIC_18_2_INTEGER
    || (
      integer === MAX_NUMERIC_18_2_INTEGER
      && hundredthsText === "99"
      && /[1-9]/.test(fraction.slice(2))
    )
  ) {
    return null;
  }
  let cents = integer * 100n + BigInt(hundredthsText);
  if ((fraction[2] ?? "0") >= "5") cents += 1n;
  if (cents > MAX_NUMERIC_18_2_CENTS) return null;
  return matched[1] === "-" ? -cents : cents;
}

/** 把合法目标毛利率转成万分比整数，避免建议报价重新进入浮点运算。 */
function marginRateUnits(value: string | number): bigint {
  const matched = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!matched || matched[2].length > 16) return 0n;
  const fraction = matched[3] ?? "";
  let units = BigInt(matched[2]) * 10_000n
    + BigInt(`${fraction}0000`.slice(0, 4));
  if ((fraction[4] ?? "0") >= "5") units += 1n;
  return matched[1] === "-" ? -units : units;
}

/** 任意有符号整数除法按 ROUND_HALF_UP 取整。 */
function divideHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) return 0n;
  const negative = (numerator < 0n) !== (denominator < 0n);
  const absoluteNumerator = absoluteBigInt(numerator);
  const absoluteDenominator = absoluteBigInt(denominator);
  const quotient = (absoluteNumerator * 2n + absoluteDenominator)
    / (absoluteDenominator * 2n);
  return negative ? -quotient : quotient;
}

/** 把整数分稳定格式化为两位小数金额，不经过 Number。 */
function formatMoneyCents(cents: bigint): string {
  const negative = cents < 0n;
  const absolute = negative ? -cents : cents;
  return `${negative ? "-" : ""}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

/** 去除金额文本的无效尾零，供公式单价与输入回显使用。 */
function formatPlainMoneyCents(cents: bigint): string {
  return formatMoneyCents(cents).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

/** 当前地区下的实际油耗单价（整数分）。 */
function busFuelEffectiveRateCents(row: BusFuelRow, region: BusFuelRegion): bigint | null {
  const regularCents = decimalToCents(row.regularRate);
  const addonCents = decimalToCents(busFuelRegionAddon(row, region)) ?? 0n;
  if (regularCents === null || regularCents < 0n) return null;
  const effectiveCents = regularCents + addonCents;
  return effectiveCents <= MAX_SAFE_BUS_MONEY_CENTS ? effectiveCents : null;
}

/** 超出数据库金额边界的派生值沿用前端既有归零策略。 */
function formatDerivedMoney(cents: bigint): string {
  return absoluteBigInt(cents) <= MAX_NUMERIC_18_2_CENTS
    ? formatMoneyCents(cents)
    : "0.00";
}

/** 判断派生金额是否仍可落入 MySQL DECIMAL(18,2)。 */
function isDerivedMoneyInRange(cents: bigint): boolean {
  return absoluteBigInt(cents) <= MAX_NUMERIC_18_2_CENTS;
}

/** 保留计税费用小数计算营业税，仅将最终税额按 ROUND_HALF_UP 舍入到分。 */
export function calculateBusinessTax(taxableAmount: string | number): {
  taxableBase: string;
  amount: string;
  formula: string;
} {
  const taxableBaseCents = decimalToCents(taxableAmount) ?? 0n;
  // 1.16 × 3.5% = 406 / 10000；直接以整数分计税，不先舍入到元。
  const taxCents = divideHalfUp(taxableBaseCents * 406n, 10_000n);
  const taxableBaseMoney = formatMoneyCents(taxableBaseCents);
  const taxAmount = formatMoneyCents(taxCents);
  return {
    taxableBase: taxableBaseMoney,
    amount: taxAmount,
    formula: `计税费用 ${taxableBaseMoney} × 1.16 × 3.5%`,
  };
}

/** 判断预算行是否为系统营业税行。 */
export function isBusinessTaxLine(line: QuoteBudgetLine): boolean {
  const itemName = String(line.itemNameSnapshot ?? "").trim();
  if (itemName === "开票税金") return false;
  return String(line.calcType ?? "").trim() === "business_tax"
    || String(line.resourceEntryType ?? "").trim() === "营业税"
    || itemName === "营业税";
}

/** 把残留系统税标记的开票税金恢复为可编辑普通杂费。 */
function normalizeManualInvoiceTaxLine(line: QuoteBudgetLine): QuoteBudgetLine {
  if (String(line.itemNameSnapshot ?? "").trim() !== "开票税金") return line;
  return {
    ...line,
    resourceType: "杂费",
    resourceEntryType: "manual",
    calcType: "snapshot",
    isAgentCollection: false,
    isLocked: false,
    priceSourceType: "manual",
    priceSourceRef: null,
  };
}

/** 税行归一保留优先级：正规系统行 > 条目类型行 > 名称别名行。 */
function businessTaxLinePriority(line: QuoteBudgetLine): number {
  if (String(line.calcType ?? "").trim() === "business_tax") return 0;
  if (String(line.resourceEntryType ?? "").trim() === "营业税") return 1;
  return 2;
}

/** 为随草稿提交的税行选择后端请求模型可接受且不重复的行号。 */
function nextSubmittedBusinessTaxLineNo(lines: QuoteBudgetLine[]): number {
  const used = new Set(lines.map((line) => line.lineNo));
  const maximum = lines.reduce((value, line) => Math.max(value, line.lineNo), 0);
  if (maximum < MAX_SAVE_DRAFT_LINE_NO) return maximum + 1;
  for (let candidate = 1; candidate <= used.size + 1; candidate += 1) {
    if (!used.has(candidate)) return candidate;
  }
  return 1;
}

/** 在普通费用之后追加或刷新系统营业税行。 */
export function withBusinessTaxLine(lines: QuoteBudgetLine[]): QuoteBudgetLine[] {
  const ordinaryLines = lines
    .filter((line) => !isBusinessTaxLine(line))
    .map(normalizeManualInvoiceTaxLine);
  if (ordinaryLines.length === 0) return ordinaryLines;
  const subtotalCents = ordinaryLines.reduce(
    (sum, line) => sum + moneyCents(calculateLineAmount(line)),
    0n,
  );
  const tax = calculateBusinessTax(formatMoneyCents(subtotalCents));
  let previous: QuoteBudgetLine | undefined;
  for (const line of lines) {
    if (!isBusinessTaxLine(line)) continue;
    if (!previous || businessTaxLinePriority(line) < businessTaxLinePriority(previous)) {
      previous = line;
    }
  }
  const taxLine: QuoteBudgetLine = {
    budgetLineId: previous?.budgetLineId ?? "system:business-tax",
    budgetOrderId: previous?.budgetOrderId,
    lineNo: nextSubmittedBusinessTaxLineNo(ordinaryLines),
    resourceType: "杂费",
    resourceEntryType: "营业税",
    itemNameSnapshot: "营业税",
    itemSpecSnapshot: "计税费用 × 1.16 × 3.5%",
    travelerRole: null,
    calcType: "business_tax",
    peopleMode: "manual",
    manualPeopleCount: null,
    isLinkedToPeopleCount: false,
    isCapitaShared: false,
    isLocked: true,
    quantity: "1",
    unit: "项",
    dayCount: null,
    nightCount: null,
    budgetUnitPrice: tax.amount,
    budgetAmount: tax.amount,
    priceSourceType: "formula",
    priceSourceRef: "business_tax:1.16:0.035",
    priceBasisDesc: `${tax.formula} = ${tax.amount}`,
    remark: previous?.remark ?? "系统自动计算",
  };
  return [...ordinaryLines, taxLine];
}

/**
 * 计算汇总：计入/不计客单金额、总成本、人均成本、建议报价。
 * @param order 预算表头（取人数与目标毛利率）
 * @param lines 预算行
 * @param options 可选覆盖分摊人数
 */
export function calculateBudgetSummary(
  order: Pick<QuoteBudgetOrder, "studentCount" | "teacherCount" | "teacherPays" | "targetMarginRate">,
  lines: QuoteBudgetLine[],
  options?: { shareCount?: number; savedAmounts?: boolean },
): QuoteBudgetSummary {
  let capitaShared = 0n;
  let nonCapita = 0n;
  for (const line of lines) {
    const amount = moneyCents(String(options?.savedAmounts
      ? (line.budgetAmount ?? calculateLineAmount(line)) : calculateLineAmount(line)));
    if (line.isCapitaShared) {
      capitaShared += amount;
    } else {
      nonCapita += amount;
    }
  }
  const total = capitaShared + nonCapita;
  if (![capitaShared, nonCapita, total].every(isDerivedMoneyInRange)) {
    return {
      capitaSharedAmount: "0.00",
      nonCapitaAmount: "0.00",
      totalCostAmount: "0.00",
      perCapitaCostAmount: "0.00",
      suggestedQuoteAmount: "0.00",
    };
  }

  const shareCount =
    options?.shareCount ??
    toNumber(order.studentCount) + (order.teacherPays === false ? 0 : toNumber(order.teacherCount));
  const shareCountDivisor = Number.isSafeInteger(shareCount) && shareCount > 0
    ? BigInt(shareCount)
    : 0n;
  const perCapita = shareCountDivisor > 0n
    ? divideHalfUp(total, shareCountDivisor)
    : 0n;

  const divisor = 10_000n - marginRateUnits(order.targetMarginRate);
  const suggested = divisor > 0n && shareCountDivisor > 0n
    ? divideHalfUp(total * 10_000n, shareCountDivisor * divisor)
    : 0n;

  return {
    capitaSharedAmount: formatDerivedMoney(capitaShared),
    nonCapitaAmount: formatDerivedMoney(nonCapita),
    totalCostAmount: formatDerivedMoney(total),
    perCapitaCostAmount: formatDerivedMoney(perCapita),
    suggestedQuoteAmount: formatDerivedMoney(suggested),
  };
}

/** 从当前工作态明细实时汇总普通费用、营业税与角色承担金额。 */
export function calculateCostBreakdown(
  order: Pick<QuoteBudgetOrder, "studentCount" | "teacherCount" | "teacherPays">,
  lines: QuoteBudgetLine[],
  auditIssues: QuoteBudgetAuditIssue[] = [],
  savedAmounts = false,
): QuoteBudgetCostBreakdown {
  const bucketAmounts = {
    student: 0n,
    teacher: 0n,
    guide: 0n,
    driver: 0n,
    unassigned: 0n,
  };
  const bucketCounts = {
    student: 0,
    teacher: 0,
    guide: 0,
    driver: 0,
    unassigned: 0,
  };
  let ordinaryAmount = 0n;
  let businessTaxAmount = 0n;

  for (const line of lines) {
    const amount = moneyCents(String(savedAmounts
      ? (line.budgetAmount ?? calculateLineAmount(line)) : calculateLineAmount(line)));
    if (isBusinessTaxLine(line)) {
      businessTaxAmount += amount;
      continue;
    }
    ordinaryAmount += amount;
    const key = line.travelerRole === "学生"
      ? "student"
      : line.travelerRole === "老师"
        ? "teacher"
        : line.travelerRole === "导游"
          ? "guide"
          : line.travelerRole === "司机"
            ? "driver"
            : "unassigned";
    bucketAmounts[key] += amount;
    bucketCounts[key] += 1;
  }

  const shareCount = toNumber(order.studentCount)
    + (order.teacherPays === false ? 0 : toNumber(order.teacherCount));
  const totalAmount = ordinaryAmount + businessTaxAmount;
  const shareCountDivisor = Number.isSafeInteger(shareCount) && shareCount > 0
    ? BigInt(shareCount)
    : 0n;
  const bucketKeys = ["student", "teacher", "guide", "driver", "unassigned"] as const;
  const amountsInRange = [
    ordinaryAmount,
    businessTaxAmount,
    totalAmount,
    ...bucketKeys.map((key) => bucketAmounts[key]),
  ].every(isDerivedMoneyInRange);
  const displayAmount = (amount: bigint): string => (
    amountsInRange ? formatMoneyCents(amount) : "0.00"
  );

  return {
    ordinaryAmount: displayAmount(ordinaryAmount),
    businessTaxAmount: displayAmount(businessTaxAmount),
    totalAmount: displayAmount(totalAmount),
    shareCount,
    perCapitaAmount: displayAmount(
      amountsInRange && shareCountDivisor > 0n
        ? divideHalfUp(totalAmount, shareCountDivisor)
        : 0n,
    ),
    buckets: bucketKeys.map((key) => ({
      key,
      amount: displayAmount(bucketAmounts[key]),
      lineCount: bucketCounts[key],
    })),
    auditIssues,
  };
}
